import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import GalleryGrid from "@/components/feature/GalleryGrid";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";
import { useVillaGalleryImages } from "@/hooks/useVillaGalleryImages";

const villas = [
  {
    slug: "villa-frangipani",
    img: img("villas/villa-frangipani/villa-frangipani-view-01.jpg"),
    nameDe: "Villa Frangipani",
    nameEn: "Villa Frangipani",
    tagDe: "2 Schlafzimmer · max. 4 Personen · 110 qm",
    tagEn: "2 bedrooms · max. 4 persons · 110 sqm",
    descDe:
      "Zwei große Zimmer, ein großes gemeinsames Badezimmer und eine große Terrasse — ideal für Paare oder Familien bis 4 Personen.",
    descEn:
      "Two large rooms, one large bathroom and a very generous terrace — ideal for couples or families of up to 4 people.",
    priceKeys: ["villa_frangipani_1", "villa_frangipani_2"],
  },
  {
    slug: "villa-bougainvillea",
    img: img("villas/villa-bougainvillea/villa-bougainvillea-view-01.jpg"),
    nameDe: "Villa Bougainvillea",
    nameEn: "Villa Bougainvillea",
    tagDe: "1 Schlafzimmer · max. 2 Personen · 78 qm",
    tagEn: "1 bedroom · max. 2 persons · 78 sqm",
    descDe:
      "Geräumiges Zimmer, großes Badezimmer und eine einladende Terrasse — die kleinste und romantischste unserer drei Villen.",
    descEn:
      "A spacious room, large bathroom and inviting terrace — the smallest and most romantic of our three villas.",
    priceKeys: ["villa_bougainvillea_1"],
  },
  {
    slug: "villa-hibiscus",
    img: img("villas/villa-hibiscus/villa-hibiscus-view-01.jpg"),
    nameDe: "Villa Hibiskus",
    nameEn: "Villa Hibiscus",
    tagDe: "2–3 Schlafzimmer · max. 6 Personen · 230 qm",
    tagEn: "2–3 bedrooms · max. 6 persons · 230 sqm",
    descDe:
      "Die größte unserer Villen: drei Zimmer mit eigenem Bad, Wohnzimmer, voll ausgestattete Küche, Speiseterrasse und ein 11×6 m Pool.",
    descEn:
      "Our largest villa: three en-suite rooms, living room, fully equipped kitchen, dining terrace and an 11×6 m pool.",
    priceKeys: ["villa_hibiskus_2", "villa_hibiskus_3"],
  },
];

export default function VillasPage() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");

  const { priceMap, money } = usePriceMap();
  const galleryImages = useVillaGalleryImages();

  const villaPrice = (keys: string[], de: boolean) => {
    const min = Math.min(...keys.map((k) => priceMap[k] ?? 0));
    const prefix = keys.length > 1;
    return de
      ? `${prefix ? "ab " : ""}${money(min)} / Nacht`
      : `${prefix ? "from " : ""}${money(min)} / night`;
  };

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("villas_eyebrow")}
          title={t("villas_title")}
          subtitle={t("villas_intro")}
          image={img("villas/header-villas-01.jpg")}
          imageAlt="Bali VW Tour – unsere Villen"
        />

        {/* Villa cards */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {villas.map((v) => (
                <div
                  key={v.slug}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-600 transition-all"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={v.img}
                      alt={isDe ? v.nameDe : v.nameEn}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 right-4 bg-background-50/95 text-foreground-900 text-xs font-semibold px-3 py-1 rounded-full">
                      {villaPrice(v.priceKeys, isDe)}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-1">
                      {isDe ? v.nameDe : v.nameEn}
                    </h3>
                    <div className="text-xs text-accent-700 mb-3">
                      {isDe ? v.tagDe : v.tagEn}
                    </div>
                    <div className="w-10 h-0.5 bg-accent-500 mb-4"></div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                      {isDe ? v.descDe : v.descEn}
                    </p>
                    <LocalizedLink
                      to={`/${v.slug}`}
                      className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                    >
                      {isDe ? "Mehr erfahren" : "Learn more"}
                      <i className="ri-arrow-right-line"></i>
                    </LocalizedLink>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-4">
              <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">
                — {t("villas_gallery_title")} —
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 leading-[1.12] tracking-tight">
                {t("villas_gallery_title")}
              </h2>
            </div>
            <p className="text-center text-sm text-foreground-600 mb-10">
              {isDe ? "Tippen Sie auf ein Bild, um es zu vergrößern." : "Tap on an image to enlarge it."}
            </p>
            <GalleryGrid images={galleryImages} />
            <div className="mt-8 text-center">
              <LocalizedLink
                to="/villa-reservation"
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 px-8 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-calendar-check-line"></i>
                {t("nav_book_now")}
              </LocalizedLink>
              <div className="mt-3 flex justify-center">
                <BookingCompanyNotice isDe={isDe} />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}